/**
 * ActorDirectImageUpload — Milestone C (AC-ACTORS-9)
 *
 * Avatar-as-control: the avatar image itself is the click/drop target.
 * Replaces the old "Drop an image here" dropzone box.
 *
 * - Click the avatar → opens file picker
 * - Drag-drop image onto avatar → sets it
 * - Client-side MIME + size validation (mirrors server AC-IMG-6)
 * - Uploads to POST /api/v1/actors/avatar-upload
 * - Returns the new attachment ID via onChange(id)
 * - Remove button → onChange(null)
 */

import React, { useCallback, useRef, useState } from 'react';
import { Camera, X, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { t } from '@/lib/i18n';
import { apiFetch } from '@/lib/api';
import { useAttachmentThumbnail, invalidateAttachmentCache } from './useAttachmentThumbnail';

// ─── Validation constants — must match server (actors.routes.ts) ───────────
const MAX_BYTES = 2 * 1024 * 1024; // 2 MB
const ALLOWED_MIME = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);

// ─── Props ─────────────────────────────────────────────────────────────────

export interface ActorDirectImageUploadProps {
  /** Current image attachment ID (null = no image). */
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
}

// ─── Component ─────────────────────────────────────────────────────────────

export function ActorDirectImageUpload({
  value,
  onChange,
  disabled = false,
}: ActorDirectImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Resolve thumbnail from the attachment cache
  const thumbnailUrl = useAttachmentThumbnail(value);

  // ─── Validation ──────────────────────────────────────────────────────────

  function validateFile(file: File): string | null {
    if (!ALLOWED_MIME.has(file.type.toLowerCase())) {
      return t('Invalid file type. Accepted: JPEG, PNG, WebP (max 2 MB).');
    }
    if (file.size > MAX_BYTES) {
      return t('Image exceeds the 2 MB size limit. Please use a smaller file.');
    }
    return null;
  }

  // ─── Upload ──────────────────────────────────────────────────────────────

  const handleFile = useCallback(async (file: File) => {
    setUploadError(null);
    const validationError = validateFile(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setIsUploading(true);
    try {
      const form = new FormData();
      form.append('avatar', file);

      const res = await apiFetch('/api/v1/actors/avatar-upload', {
        method: 'POST',
        body: form,
      });

      const json = await res.json();

      if (!res.ok || !json.success) {
        const msg = json?.error?.message
          ?? t('Upload failed. Please try again.');
        setUploadError(msg);
        return;
      }

      // Invalidate cache so useAttachmentThumbnail picks up the new record
      invalidateAttachmentCache();
      onChange(json.data.id);
    } catch {
      setUploadError(t('Upload failed. Please check your connection and try again.'));
    } finally {
      setIsUploading(false);
    }
  }, [onChange]);

  // ─── Input / drag-drop handlers ──────────────────────────────────────────

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    // Reset the input value so the same file can be re-selected after removal
    e.target.value = '';
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || isUploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }, [disabled, isUploading, handleFile]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled && !isUploading) setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setUploadError(null);
    onChange(null);
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-2" data-testid="actor-direct-image-upload">
      {/* Avatar — the entire element is the click/drop target */}
      <div
        role="button"
        tabIndex={disabled || isUploading ? -1 : 0}
        aria-label={t('Upload actor avatar — click or drag an image here')}
        className={[
          'relative w-20 h-20 rounded-xl overflow-hidden border-2 cursor-pointer transition-colors group flex-shrink-0',
          isDragging
            ? 'border-primary bg-primary/10'
            : 'border-border hover:border-primary/60',
          disabled || isUploading ? 'opacity-50 cursor-not-allowed pointer-events-none' : '',
        ].join(' ')}
        onClick={() => !disabled && !isUploading && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled && !isUploading) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        data-testid="actor-avatar-dropzone"
      >
        {/* Image or placeholder */}
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={t('Actor avatar preview')}
            className="w-full h-full object-cover"
            data-testid="actor-avatar-upload-thumbnail"
            onError={(e) => {
              // negative: broken image → hide, no broken-image glyph
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center" data-testid="actor-avatar-upload-preview">
            <ImageIcon className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
          </div>
        )}

        {/* Hover overlay — camera icon */}
        {!isUploading && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera className="h-5 w-5 text-white" aria-hidden="true" />
          </div>
        )}

        {/* Uploading spinner overlay */}
        {isUploading && (
          <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Remove button (only when an image is set) */}
      {value && !disabled && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          leftIcon={<X className="h-3.5 w-3.5" />}
          onClick={handleRemove}
          disabled={isUploading}
          className="!text-destructive hover:!bg-destructive/10 h-7 text-xs"
          data-testid="actor-avatar-remove-btn"
        >
          {t('Remove image')}
        </Button>
      )}

      {/* Inline error */}
      {uploadError && (
        <p
          className="text-sm text-destructive"
          role="alert"
          data-testid="actor-avatar-upload-error"
        >
          {uploadError}
        </p>
      )}

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        className="sr-only"
        onChange={handleInputChange}
        disabled={disabled || isUploading}
        data-testid="actor-avatar-file-input"
        aria-hidden="true"
      />
    </div>
  );
}
